import {
  getCountryFromAccountNumber,
  isEecsAccountIdentifier,
  isOfgemAccountIdentifier,
} from "@/utils/eecs";

export type RegistrationAuthority = "AIB" | "REGO" | "RGP";

const RGP_ACCOUNT_PATTERN = /^[A-Z0-9]+$/;

export const AUTHORITY_FLAGS: Record<RegistrationAuthority, string> = {
  AIB: "🇪🇺",
  REGO: "🇬🇧",
  RGP: "🇵🇱",
};

export const AUTHORITY_ACCOUNT_MESSAGE_KEYS: Record<
  RegistrationAuthority,
  string
> = {
  AIB: "common.counterparty_picker.account_invalid_aib",
  REGO: "common.counterparty_picker.account_invalid_rego",
  RGP: "common.counterparty_picker.account_invalid_rgp",
};

const REGION_AUTHORITIES: Record<string, RegistrationAuthority> = {
  GB: "REGO",
  PL: "RGP",
};

/**
 * Registration authority governing the registry a certificate sits in.
 *
 * NOTE: mirrors registration_authority_for_country in
 * app/registry/registration_authorities.py, narrowed to the schemes Soldera connects
 * to. Every other region we hold certificates in is an AIB member, and sub-region codes
 * ("BE-FLA") resolve through their country, so AIB is the correct fallback.
 */
export function registrationAuthorityForRegion(
  region: string | null | undefined
): RegistrationAuthority {
  const country = (region ?? "").toUpperCase().split("-")[0];
  return REGION_AUTHORITIES[country] ?? "AIB";
}

/**
 * NOTE: mirrors the backend validator in
 * app/registry/registration_authorities.py. An empty value is valid because
 * the field is optional; an unknown (null) authority is valid because the
 * expected format is unknowable until an authority is derived.
 */
export function isValidRegistryAccountNumber(
  value: string | null | undefined,
  authority: RegistrationAuthority | null | undefined
): boolean {
  if (!value || !authority) return true;
  if (authority === "AIB") return isEecsAccountIdentifier(value);
  if (authority === "REGO") return isOfgemAccountIdentifier(value);
  return RGP_ACCOUNT_PATTERN.test(value.toUpperCase());
}

/**
 * NOTE: an RGP account is a TGE member code with no embedded country signal,
 * so under RGP a valid value resolves to PL by construction (TGE is the
 * Polish registry). Other/unknown authorities derive the country from the
 * value shape (EECS member-code prefix, Ofgem -> GB).
 */
export function registryCountryForAccount(
  value: string | null | undefined,
  authority: RegistrationAuthority | null | undefined
): string | null {
  if (!value) return null;
  if (authority === "RGP") {
    return isValidRegistryAccountNumber(value, "RGP") ? "PL" : null;
  }
  return getCountryFromAccountNumber(value);
}
